import { ImgID } from "../../image_loader/img_id.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { hapticsController } from "../../haptics/haptics_controller.js";
import { VersionLoader } from "../../version_loader.js";
import { AppConfigService } from "../../app_config/app_config_service.js";
import { TelegramAPISettings } from "../../telegram_api_settings.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { Vector2 } from "../../common/vector2.js";

export class SettingsDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 909;
  private static readonly DESIGN_H = 1105;
  private static readonly TERMS_URL = "https://match-star.com/terms-of-use.html";
  private static readonly PRIVACY_URL = "https://match-star.com/policy.html";
  private static readonly TOGGLE_COLUMN_GAP = 282;
  private static readonly TOGGLE_LABEL_Y = 340;
  private static readonly TOGGLE_LABEL_W = 270;
  private static readonly TOGGLE_LABEL_H = 70;
  private static readonly TOGGLE_LABEL_FONT = 45;
  private static readonly TOGGLE_BG_Y = 410;
  private static readonly TOGGLE_BG_SIZE = 218;
  private static readonly TOGGLE_ICON_SIZE = 160;

  private close_btn!: UIImage;

  constructor()
  {
    const body_w = LayoutScaler.widthFromDesign( SettingsDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( SettingsDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTitle();
    this.createDivider( 273 );

    const center_x = SettingsDialog.DESIGN_W / 2;
    const column_gap = SettingsDialog.TOGGLE_COLUMN_GAP;

    this.createToggleButton( center_x - column_gap, "SETTINGS.SOUNDS", () => SettingsDialog.soundImg(), async () =>
    {
      const next = !audioController.is_sounds_enabled;
      if ( next )
      {
        await audioController.enableSounds( true );
        void audioController.playSound( AudioClips.UI_CLICK );
      }
      else
      {
        void audioController.playSound( AudioClips.UI_CLICK );
        await audioController.enableSounds( false );
      }
    } );

    this.createToggleButton( center_x, "SETTINGS.MUSIC", () => SettingsDialog.musicImg(), async () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      await audioController.enableMusic( !audioController.is_music_enabled );
    } );

    this.createToggleButton( center_x + column_gap, "SETTINGS.VIBRATION", () => SettingsDialog.vibrationImg(), async () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      const next = !hapticsController.isHapticsEnabled();
      hapticsController.enableHaptics( next );
      if ( next )
        await hapticsController.vibratePreview();
    } );

    this.createDivider( 690 );

    const btn_h = 151;
    this.createMenuButton( 755, btn_h, "SETTINGS.GROUP", () => SettingsDialog.openCommunity() );
    this.createCloseButton();
    this.createVersionRow();
  }

  // The only background in the dialog: the dark blue capsule image, covering
  // just X0 Y88 W909 H1057. Everything outside that rect (the close-button
  // strip at the top, and the margin below) stays fully transparent.
  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 88 ),
      LayoutScaler.widthFromDesign( 909 ),
      LayoutScaler.heightFromDesign( 937 ),
      ImgID.UI_CapsuleBlue,
      "settings_dialog_capsule_bg"
    );
    this.addChild( bg );
  }

  private createTitle(): void
  {
    const title_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 143 ),
      LayoutScaler.widthFromDesign( SettingsDialog.DESIGN_W ),
      LayoutScaler.heightFromDesign( 109 ),
      {
        localization_key: "SETTINGS",
        font_size: LayoutScaler.textSizeFromDesign( 62 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( title_text );
  }

  private createDivider( y: number ): void
  {
    const design_w = 625;
    const design_h = 11;
    const design_x = ( SettingsDialog.DESIGN_W - design_w ) / 2;

    const divider_x = LayoutScaler.xFromDesign( design_x );
    const divider_y = LayoutScaler.yFromDesign( y );
    const divider_w = LayoutScaler.widthFromDesign( design_w );
    const divider_h = LayoutScaler.heightFromDesign( design_h );
    const divider_radius = LayoutScaler.cornerRadiusFromDesign( 6 );

    const divider = new RoundedRectangle( this.ctx, divider_x, divider_y, divider_w, divider_h, divider_radius, ColorScheme.settings_dialog_divider );
    this.addChild( divider );
  }

  private static soundImg(): ImgID
  {
    return audioController.is_sounds_enabled ? ImgID.UI_GameSoundOn : ImgID.UI_GameSoundOff;
  }

  private static musicImg(): ImgID
  {
    return audioController.is_music_enabled ? ImgID.UI_GameMusicOn : ImgID.UI_GameMusicOff;
  }

  private static vibrationImg(): ImgID
  {
    return hapticsController.isHapticsEnabled() ? ImgID.UI_GameVibrationOn : ImgID.UI_GameVibrationOff;
  }

  private createToggleButton(
    center_x: number,
    localization_key: string,
    get_img: () => ImgID,
    on_toggle: () => Promise<void>
  ): void
  {
    const label_x = LayoutScaler.xFromDesign( center_x - SettingsDialog.TOGGLE_LABEL_W / 2 );
    const label_y = LayoutScaler.yFromDesign( SettingsDialog.TOGGLE_LABEL_Y );
    const label_w = LayoutScaler.widthFromDesign( SettingsDialog.TOGGLE_LABEL_W );
    const label_h = LayoutScaler.heightFromDesign( SettingsDialog.TOGGLE_LABEL_H );

    const label_text = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( SettingsDialog.TOGGLE_LABEL_FONT ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.addChild( label_text );

    const bg_x = LayoutScaler.xFromDesign( center_x - SettingsDialog.TOGGLE_BG_SIZE / 2 );
    const bg_y = LayoutScaler.yFromDesign( SettingsDialog.TOGGLE_BG_Y );
    const bg_w = LayoutScaler.widthFromDesign( SettingsDialog.TOGGLE_BG_SIZE );
    const bg_h = LayoutScaler.heightFromDesign( SettingsDialog.TOGGLE_BG_SIZE );

    const bg = new UIImage( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_SettingsIconBg, undefined, true );
    this.addChild( bg );

    const icon_w = LayoutScaler.widthFromDesign( SettingsDialog.TOGGLE_ICON_SIZE );
    const icon_h = LayoutScaler.heightFromDesign( SettingsDialog.TOGGLE_ICON_SIZE );
    const icon_x = ( bg_w - icon_w ) / 2;
    const icon_y = ( bg_h - icon_h ) / 2 - LayoutScaler.yFromDesign( 3 );

    const icon = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, get_img() );
    icon.setClickable( false );
    bg.addChild( icon, true );

    bg.setOnClick( async () =>
    {
      await on_toggle();
      icon.setImageId( get_img() );
    } );
  }

  private static openUrl( url: string ): void
  {
    window.open( url, "_blank", "noopener,noreferrer" );
  }

  private static openCommunity(): void
  {
    const url = AppConfigService.get().community_url;
    if ( !url )
    {
      console.warn( "[Settings] community_url is not set in the app config" );
      return;
    }

    TelegramAPISettings.openLink( url );
  }

  private createMenuButton( y: number, h: number, localization_key: string, on_click: () => void ): void
  {
    const w = 800;
    const x = ( SettingsDialog.DESIGN_W - w ) / 2;

    const btn_x = LayoutScaler.xFromDesign( x );
    const btn_y = LayoutScaler.yFromDesign( y );
    const btn_w = LayoutScaler.widthFromDesign( w );
    const btn_h = LayoutScaler.heightFromDesign( h );

    const btn = new UINineSliceElement(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_SettingsLinkBtn,
      "settings_link_btn",
      undefined,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      on_click();
    } );
    this.addChild( btn );

    const MAGIC_NUM_CORRECTION = 0.85;
    const text_w = btn_w * MAGIC_NUM_CORRECTION;
    const text_x = ( btn_w - text_w ) / 2;

    const text = new UITextElement(
      this.ctx,
      text_x,
      0,
      text_w,
      btn_h,
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( 55 ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    btn.addChild( text, true );
  }
  private createVersionRow(): void
  {
    const version = VersionLoader.getVersion();

    const version_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 414 ),
      LayoutScaler.yFromDesign( 1176 ),
      LayoutScaler.widthFromDesign( 400 ),
      LayoutScaler.heightFromDesign( 69 ),
      {
        text: `ver: ${version}`,
        font_size: LayoutScaler.textSizeFromDesign( 36 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    version_text.setOpacity( 0.61 );
    this.addChild( version_text );

    const copy_btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 350 ),
      LayoutScaler.yFromDesign( 1192 ),
      LayoutScaler.widthFromDesign( 38 ),
      LayoutScaler.heightFromDesign( 39 ),
      ImgID.UI_SettingsDialogCopyBtn,
      undefined,
      true
    );
    copy_btn.setOpacity( 0.61 );
    copy_btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      void navigator.clipboard?.writeText( version );
    } );
    this.addChild( copy_btn );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;

  
    const design_btn_x = 90;
    const btn_x = SettingsDialog.DESIGN_W - design_btn_x;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( btn_x  ),
      LayoutScaler.yFromDesign( 50 ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
    } );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public name(): string
  {
    return "SettingsDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
